import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { TbEdit, TbCheck } from 'react-icons/tb';
import CommentDeleteButton from '../CommentDeleteButton';
import * as Styled from './style';
import { useEditComment } from '../../../../../../services/comment.service';
import { useUserGroupsCurrentUserAdminState } from '../../../../../../services/user.service';
import type { SchemaCommentMessageResponseSchema } from '../../../../../../api/generated/schema';
import { extractApiError } from '../../../../../../utils';

const COMMENT_EDITING_STATES = {
  EDIT: 'EDIT',
  EDITING: 'EDITING'
} as const;

interface CellCommentProps {
  comment: SchemaCommentMessageResponseSchema;
  totalComments: number;
  refetchComments: () => Promise<unknown>;
}

interface CurrentUser {
  azure_object_id?: string | null;
}

const CellComment = ({ comment, totalComments, refetchComments }: CellCommentProps) => {
  const [commentEditingState, setCommentEditingState] = useState<string>(
    COMMENT_EDITING_STATES.EDIT
  );
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const editCommentMutation = useEditComment();
  const { data: user } = useUserGroupsCurrentUserAdminState() as { data?: CurrentUser };
  const isCurrentUserCommentOwner = user?.azure_object_id === comment.author_id;

  const handleEditCommentClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (commentEditingState === COMMENT_EDITING_STATES.EDITING) {
      if (!textAreaRef.current?.value?.trim().length) {
        toast.error('Comment cannot be empty');
        return;
      }

      editCommentMutation.mutate(
        {
          data: { text: textAreaRef.current.value },
          threadID: comment.thread_id,
          commentID: comment.id
        },
        {
          onSuccess: () => {
            refetchComments();
            setCommentEditingState(COMMENT_EDITING_STATES.EDIT);
          },
          onError: (err: any) => {
            toast.error(extractApiError(err, 'Failed to edit this comment.'));
          }
        }
      );
    } else {
      setCommentEditingState(COMMENT_EDITING_STATES.EDITING);
    }
  };

  useEffect(() => {
    if (commentEditingState === COMMENT_EDITING_STATES.EDITING && textAreaRef.current) {
      const el = textAreaRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length); // ✅ puts cursor at end
    }
  }, [commentEditingState]);

  return (
    <Styled.CommentMessage id={`cell-comment-message-${comment.id}`}>
      <div className="comment-message-header" id={`cell-comment-message-header-${comment.id}`}>
        <h4 id={`cell-comment-author-${comment.id}`}>{comment.author_name}</h4>
        <span id={`cell-comment-created-at-${comment.id}`}>
          {moment.utc(comment.created_at).fromNow()}
        </span>
        {isCurrentUserCommentOwner && (
          <Styled.EditCommentBtn
            id={`edit-comment-button-${comment.id}`}
            data-testid={`edit-comment-btn-${comment.id}`}
            onClick={handleEditCommentClick}
          >
            {commentEditingState === COMMENT_EDITING_STATES.EDITING ? <TbCheck /> : <TbEdit />}
          </Styled.EditCommentBtn>
        )}
        {isCurrentUserCommentOwner && (
          <CommentDeleteButton
            threadCommentsCount={totalComments}
            threadID={comment.thread_id}
            commentID={comment.id}
            refetchComments={refetchComments}
          />
        )}
      </div>
      {commentEditingState === COMMENT_EDITING_STATES.EDITING ? (
        <textarea
          id={`edit-comment-textarea-${comment.id}`}
          data-testid="edit-comment-textarea"
          onPointerDown={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          ref={textAreaRef}
          defaultValue={comment.text}
          // @ts-ignore
          multiple
        />
      ) : (
        <p id={`comment-description-${comment.id}`} data-testid="comment-description">
          {textAreaRef.current?.value || comment.text}
        </p>
      )}
    </Styled.CommentMessage>
  );
};

export default CellComment;
